import { type Ref, getCurrentScope, onScopeDispose, ref, watch } from 'vue'

// Duration used before the first real measurement and whenever the track
// width is unmeasurable (jsdom, hidden track). Matches the CSS fallback in
// MarqueeRow's `var(--marquee-duration, 30s)`.
const FALLBACK_DURATION_MS = 30000

interface UseMarqueeDurationOptions {
  trackRef: Ref<HTMLElement | null>
  // Target scroll speed of the row, in pixels per second.
  speedPxPerSec: number
}

interface UseMarqueeDurationReturn {
  durationMs: Ref<number>
}

/**
 * Derives the marquee animation duration from the real track width so the
 * row scrolls at an exact, constant speed: duration = halfWidth / speed.
 *
 * The width is re-measured through a ResizeObserver, so the speed stays
 * exact across rotation, window resize, keyboard open/close and late
 * webfont loads. The observer's first callback doubles as the initial
 * measurement; the fallback duration covers the frames before it fires.
 */
export function useMarqueeDuration(
  options: UseMarqueeDurationOptions,
): UseMarqueeDurationReturn {
  const { trackRef, speedPxPerSec } = options

  const durationMs = ref(FALLBACK_DURATION_MS)

  let observer: ResizeObserver | null = null

  function measure(track: HTMLElement) {
    const halfWidth = track.scrollWidth / 2
    if (halfWidth <= 0) return

    durationMs.value = (halfWidth / speedPxPerSec) * 1000
  }

  function observe(track: HTMLElement) {
    if (typeof ResizeObserver === 'undefined') return

    observer?.disconnect()
    observer = new ResizeObserver(() => measure(track))
    observer.observe(track)
  }

  const stopWatch = watch(
    trackRef,
    (track) => {
      if (!track) return
      measure(track)
      observe(track)
    },
    { flush: 'post', immediate: true },
  )

  if (getCurrentScope()) {
    onScopeDispose(() => {
      observer?.disconnect()
      stopWatch()
    })
  }

  return { durationMs }
}
